import { ResultsView } from '@kijiji/generated/graphql-types'
import dynamic from 'next/dynamic'
import { useTheme } from 'styled-components'

import { SrpResults } from '@/components/srp/results-sort/results'
import { getAvailableViewOptions } from '@/components/srp/results-sort/sort/getAvailableViewOptions'
import {
  SrpResultsCountContainer,
  SrpResultsSortActions,
  SrpResultsSortContainer,
  SrpSortWrapper,
} from '@/components/srp/results-sort/styled'
import { SRPViewButton } from '@/features/map/components/srp-view-button/SRPViewButton'
import { useGetSrpState } from '@/features/search/hooks/useGetSrpState'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useSrpMwebRedesign } from '@/hooks/srp/useSrpMwebRedesign'
import { RemoteParamKeys, useExperiment, useToggle } from '@/lib/firebase/hooks'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { Flex } from '@/ui/atoms/flex'
import { Spacing } from '@/ui/atoms/spacing'
import { SelectVariant } from '@/ui/molecules/select'
import { capitalizeString } from '@/utils/string'

export type SrpResultsSortProps = {
  categoryId: number
  /**
   * Defines if this component will return the change in SRP view only instead of the entire header
   * TODO: Separate the logic from the toggle of this SrpResultSort component so it can be used solo without code duplication
   */
  showViewToggleOnly?: boolean
}

const SrpSort = dynamic(() => import('./sort').then((mod) => mod.SrpSort), { ssr: false })

export const SrpResultsSort = ({ categoryId, showViewToggleOnly }: SrpResultsSortProps) => {
  const { spacing } = useTheme()
  const { viewType, updatePageViewType } = useGetSrpState()
  const { location } = useGetLocation()

  /**
   * TOGGLES IN USE:
   * - SRP_CONTROLS_MWEB_REDESIGN
   * - SRP_GRID_VIEW_TOGGLE
   * EXPERIMENTS IN USE:
   * - SRP_REAL_ESTATE_GRID_TEST
   */
  const gridViewExpRealEstate = useExperiment(RemoteParamKeys.SRP_REAL_ESTATE_GRID_TEST)
  const isRealEstateGridExpEnabled =
    (gridViewExpRealEstate?.enabled && gridViewExpRealEstate?.group === 1) ?? false
  const isGridViewEnabled = useToggle(RemoteParamKeys.SRP_GRID_VIEW_TOGGLE)?.enabled
  const isSrpControlsMwebToggleEnabled = useSrpMwebRedesign()

  const availableViewTypes = getAvailableViewOptions({
    categoryId,
    currentView: viewType,
    isGridViewEnabled,
    isRealEstateGridExpEnabled,
    location,
  })

  const handleToggleView = (newView: ResultsView) => {
    updatePageViewType(newView)
    handleTrackEventCTA(newView)
  }

  const handleTrackEventCTA = (newView: ResultsView) => {
    const trackingLabel = capitalizeString(newView)
    trackEvent({ action: GA_EVENT.PageViewSwitchSuccess, label: `cta=${trackingLabel}` })
  }

  const isMapView = viewType === ResultsView.Map

  const viewToggleAvailableButtons = availableViewTypes.map((availableViewType) => (
    <SRPViewButton
      key={`available-view-type-${availableViewType}`}
      onClick={handleToggleView}
      type={availableViewType}
      isSrpControlsMwebToggleEnabled={isSrpControlsMwebToggleEnabled}
    />
  ))

  if (showViewToggleOnly)
    return (
      <Flex gap={spacing.default}>
        {availableViewTypes.length ? (
          <Spacing
            mTop={isSrpControlsMwebToggleEnabled ? '0' : undefined}
            mBottom={isSrpControlsMwebToggleEnabled ? undefined : spacing.defaultSmall}
            medium={isSrpControlsMwebToggleEnabled ? { mTop: '0' } : undefined}
          >
            {viewToggleAvailableButtons}
          </Spacing>
        ) : null}
      </Flex>
    )

  return (
    <SrpResultsSortContainer
      isMapView={isMapView}
      isSrpControlsMwebToggleEnabled={isSrpControlsMwebToggleEnabled}
    >
      <SrpResultsCountContainer>
        <SrpResults />
      </SrpResultsCountContainer>

      <SrpResultsSortActions
        // All the available view types + sorting action
        availableActionsLength={availableViewTypes.length + 1}
        isMapView={isMapView}
        isSrpControlsMwebToggleEnabled={isSrpControlsMwebToggleEnabled}
      >
        {availableViewTypes.length ? (
          <Spacing
            medium={isSrpControlsMwebToggleEnabled ? { mTop: '0' } : undefined}
            small={isSrpControlsMwebToggleEnabled ? { mTop: '0' } : undefined}
          >
            {viewToggleAvailableButtons}
          </Spacing>
        ) : null}

        <SrpSortWrapper
          isMapView={isMapView}
          isSrpControlsMwebToggleEnabled={isSrpControlsMwebToggleEnabled}
        >
          <SrpSort variant={SelectVariant.ROUNDED} />
        </SrpSortWrapper>
      </SrpResultsSortActions>
    </SrpResultsSortContainer>
  )
}
